import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI, authAPI, clearGetProfileCache, clearAuthTokenCaches } from './api';
import { clearClientStorage } from './utils/browserStorage';
import { clearTokenCache } from './utils/tokenUtils';
import { t } from './i18n';


/**
 * Хук для обработки кликов вне заданного элемента.
 * @param {React.RefObject} ref - Реф элемента, для которого нужно отслеживать клик вне.
 * @param {Function} onOutsideClick - Callback, который вызывается при клике вне элемента.
 */
export const useOutsideClick = (ref, onOutsideClick) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onOutsideClick]);
};


// Кеш для профиля пользователя
let profileCache = null;
let profileCacheTime = 0;
const PROFILE_CACHE_DURATION = 30000; // Кешируем на 30 секунд

// Функция для очистки кеша профиля (например, после изменения настроек)
export const clearProfileCache = () => {
  profileCache = null;
  profileCacheTime = 0;
  // Также очищаем кеш на уровне API
  clearGetProfileCache();
};

export const fetchUserProfile = async () => {
  const now = Date.now();
  
  // Если есть валидный кеш, возвращаем его
  if (profileCache && (now - profileCacheTime) < PROFILE_CACHE_DURATION) {
    return profileCache;
  }
  
  const response = await profileAPI.getProfile();
  
  // Если данные приходят в виде строки JSON, парсим их
  let profileData;
  if (typeof response.data === 'string') {
    try {
      profileData = JSON.parse(response.data);
    } catch (e) {
      throw e;
    }
  } else if (typeof response.data === 'object' && response.data !== null) {
    profileData = response.data;
  } else {
  throw new Error('Unexpected response format');
  }
  
  // Кешируем профиль
  profileCache = profileData;
  profileCacheTime = now;
  
  return profileData;
};


export const isAuthenticated = async () => {
  const now = Date.now();
  if (profileCache && (now - profileCacheTime) < PROFILE_CACHE_DURATION) {
    return true;
  }

  try {
    const response = await profileAPI.getProfile(true);
    if (response.data) {
      profileCache = typeof response.data === 'object' ? response.data : JSON.parse(response.data);
      profileCacheTime = now;
      return true;
    }
    return false;
  } catch (error) {
    if (!error.response) {
      return false;
    }

    if (error.response?.status === 401) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      try {
        const retryResponse = await profileAPI.getProfile(true);
        if (retryResponse.data) {
          profileCache = typeof retryResponse.data === 'object'
            ? retryResponse.data
            : JSON.parse(retryResponse.data);
          profileCacheTime = Date.now();
          return true;
        }
      } catch (retryError) {
        if (retryError.response?.status === 401) {
          profileCache = null;
          profileCacheTime = 0;
        }
        return false;
      }
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      profileCache = null;
      profileCacheTime = 0;
    }
    return false;
  }
};


export const useLogout = () => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Игнорируем ошибки при выходе
    } finally {
      // Токены удаляются сервером автоматически через куки
      clearClientStorage();
      clearGetProfileCache();
      clearAuthTokenCaches();
      clearTokenCache();
      profileAPI.clearAvatarCache();
      // Очищаем кеш профиля
      profileCache = null;
      profileCacheTime = 0;
      window.dispatchEvent(new CustomEvent('userLoggedOut'));
      navigate('/');
    }
  };

  return handleLogout;
};



/**
 * Нормализует строку даты из бэкенда, добавляя 'Z' если его нет.
 * Это гарантирует, что JavaScript интерпретирует время как UTC,
 * а затем автоматически конвертирует в локальный часовой пояс пользователя.
 * 
 * @param {string} dateString - Строка даты в ISO формате
 * @returns {string} - Нормализованная строка с 'Z' суффиксом
 */
const normalizeUtcDateString = (dateString) => {
  if (!dateString) return dateString;
  
  // Преобразуем объект Date в строку ISO
  if (dateString instanceof Date) {
    dateString = dateString.toISOString();
  }
  
  // Преобразуем число (timestamp) в строку ISO
  if (typeof dateString === 'number') {
    dateString = new Date(dateString).toISOString();
  }
  
  // Проверяем, что это строка
  if (typeof dateString !== 'string') {
    // Если это не строка и не Date и не число, возвращаем текущую дату
    return new Date().toISOString();
  }
  
  // Если уже есть 'Z' или offset (+/-), возвращаем как есть
  if (dateString.endsWith('Z') || dateString.includes('+') || dateString.match(/-\d{2}:\d{2}$/)) {
    return dateString;
  }
  
  // Добавляем 'Z' для явного указания UTC
  return dateString + 'Z';
};

export const formatDate = (createdAt) => {
  const normalizedDate = normalizeUtcDateString(createdAt);
  const date = normalizedDate ? new Date(normalizedDate) : new Date();

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  // Всегда показываем только время (дата отображается в разделителях)
  return `${hours}:${minutes}`;
};

export const formatChatListTime = (createdAt) => {
  if (!createdAt) {
    return '';
  }
  const normalizedDate = normalizeUtcDateString(createdAt);
  const date = normalizedDate ? new Date(normalizedDate) : new Date();
  const today = new Date();

  if (date.toDateString() === today.toDateString()) {
    return formatDate(createdAt);
  }

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return t('time.yesterday');
  }

  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
};

// Функция для форматирования только даты (без времени)
export const formatDateOnly = (createdAt) => {
  const normalizedDate = normalizeUtcDateString(createdAt);
  const date = normalizedDate ? new Date(normalizedDate) : new Date();
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return t('time.today');
  }

  if (date.toDateString() === yesterday.toDateString()) {
    return t('time.yesterday');
  }

  const monthName = t(`time.months.${date.getMonth()}`);
  return t('time.monthDay', { month: monthName, day: date.getDate() });
};

// Функция для получения инициалов из имени
export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Функция для получения двух первых букв из имени (игнорируя пробелы)
export const getFirstTwoLetters = (name) => {
  if (!name) return '??';
  // Убираем все пробелы и берем первые две буквы
  const nameWithoutSpaces = name.replace(/\s+/g, '');
  if (nameWithoutSpaces.length >= 2) {
    return nameWithoutSpaces.substring(0, 2).toUpperCase();
  }
  // Если меньше двух букв, дополняем или возвращаем что есть
  return nameWithoutSpaces.toUpperCase().padEnd(2, '?');
};

// Функция для получения цвета аватара на основе имени
export const getAvatarColor = (name) => {
  if (!name) return '#9e9e9e';
  const colors = [
    '#f0f0f0', '#e0e0e0', '#d0d0d0', '#c0c0c0',
    '#b0b0b0', '#a0a0a0', '#909090', '#808080',
    '#707070', '#606060', '#585858', '#505050',
    '#484848', '#404040', '#383838', '#303030',
    '#d8d8d8', '#c8c8c8', '#b8b8b8', '#a8a8a8',
    '#989898', '#888888', '#787878', '#686868',
    '#989898', '#878787', '#767676', '#656565',
    '#545454', '#434343', '#323232', '#212121',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Функция для валидации пароля
// Требования: минимум 8 символов, цифры, заглавные и строчные английские буквы
export const validatePassword = (password) => {
  if (!password) {
    return { valid: false, message: t('validation.passwordRequired') };
  }
  
  if (password.length < 8) {
    return { valid: false, message: t('validation.passwordMinLength') };
  }
  
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: t('validation.passwordNeedNumber') };
  }
  
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: t('validation.passwordNeedLower') };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: t('validation.passwordNeedUpper') };
  }
  
  return { valid: true, message: '' };
};

// Требования: a-z, A-Z, 0-9, _ и -
export const validateUsername = (username) => {
  if (!username) {
    return { valid: true, message: '' }; // Пустое имя допустимо (будет сгенерировано автоматически)
  }
  
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    return { valid: false, message: t('validation.usernameCharset') };
  }
  
  return { valid: true, message: '' };
};

// Функция для валидации email
// Проверяет базовый формат email адреса
export const validateEmail = (email) => {
  if (!email) {
    return { valid: false, message: t('validation.emailRequired') };
  }
  
  // Базовая проверка формата email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return { valid: false, message: t('validation.emailInvalid') };
  }
  
  return { valid: true, message: '' };
};

// Функция для debounce (задержка выполнения функции)
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Функция для санитизации строки (защита от XSS)
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

// Функция для проверки honeypot поля (защита от ботов)
export const checkHoneypot = (honeypotValue) => {
  // Если honeypot поле заполнено, это бот
  return !honeypotValue || honeypotValue.trim() === '';
};

/**
 * User-facing copy: English, first letter lowercase.
 */
export const formatUserMessage = (text) => {
  if (text == null) {
    return '';
  }
  const value = String(text).trim();
  if (!value) {
    return value;
  }
  return value.charAt(0).toLowerCase() + value.slice(1);
};

/**
 * Извлекает сообщение об ошибке из ответа API.
 * Поддерживает новый формат ошибок бекенда: { success: false, msg: "...", error_code: ... }
 * Также поддерживает старый формат для обратной совместимости.
 *
 * @param {Error} error - Объект ошибки от axios
 * @param {string} defaultMessage - Сообщение по умолчанию, если не удалось извлечь
 * @returns {string} - Сообщение об ошибке
 */
export const getErrorMessage = (error, defaultMessage) => {
  const fallback = defaultMessage ?? t('validation.defaultError');
  if (!error) return formatUserMessage(fallback);
  
  // Новый формат: error.response.data.msg
  if (error.response?.data?.msg) {
    return formatUserMessage(error.response.data.msg);
  }
  
  // Старый формат для обратной совместимости: error.response.data.detail
  if (error.response?.data?.detail) {
    // Если detail - массив (валидационные ошибки FastAPI)
    if (Array.isArray(error.response.data.detail)) {
      const firstError = error.response.data.detail[0];
      if (firstError?.msg) {
        return formatUserMessage(firstError.msg);
      }
      if (typeof firstError === 'string') {
        return formatUserMessage(firstError);
      }
    }
    // Если detail - строка
    if (typeof error.response.data.detail === 'string') {
      return formatUserMessage(error.response.data.detail);
    }
  }
  
  // Еще один старый формат: error.response.data.message
  if (error.response?.data?.message) {
    return formatUserMessage(error.response.data.message);
  }
  
  // Если есть общее сообщение об ошибке
  if (error.message) {
    return formatUserMessage(error.message);
  }
  
  return formatUserMessage(fallback);
};